import { Ref, ref, watch } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { AssignPlayerOnPitchTable } from '@desktop/utils/database'
import { useEvent, useIncidents } from '@desktop/views/Relation/composables'
import { injectAssignPlayerOnPitchConfig } from '@desktop/views/Relation/Sports/provide-inject/assignPlayerOnPitchConfig'

export function useAssignPlayerOnPitch(): {
  init: () => Promise<void>
  currentIncident: Ref<Incident | null>
} {
  // inject
  const { assignPlayerOnPitchIncidents } = injectAssignPlayerOnPitchConfig()

  // composables
  const { event } = useEvent()
  const { incidentsUpdate, getIncident } = useIncidents()

  // state
  const currentIncident = ref<Incident | null>(null)

  // methods
  function isAssignPlayerOnPitchIncident<T extends IncidentId>(
    incidentId: T,
  ): boolean {
    return assignPlayerOnPitchIncidents.has(incidentId) // TODO: fix with inject ?
  }

  function hasParticipantName(incident: Incident): boolean {
    return !!incident.participant_name
  }

  function setIncident(incident: Incident): void {
    currentIncident.value = incident
    AssignPlayerOnPitchTable.setIncident(event.value.id, incident.id)
  }

  function deleteIncident(): void {
    currentIncident.value = null
    AssignPlayerOnPitchTable.remove(event.value.id)
  }

  async function init(): Promise<void> {
    const assingPlayerOnPitchTableRecord = await AssignPlayerOnPitchTable.get(
      event.value.id,
    )

    if (assingPlayerOnPitchTableRecord) {
      const incident = getIncident(assingPlayerOnPitchTableRecord.incidentId)

      if (hasParticipantName(incident)) {
        deleteIncident()
      } else {
        currentIncident.value = incident
      }
    }

    watch(incidentsUpdate, (incidentMessages) => {
      for (const incidentMessage of incidentMessages) {
        switch (incidentMessage.data.action) {
          case 'insert':
            if (
              isAssignPlayerOnPitchIncident(incidentMessage.data.incident_id)
            ) {
              setIncident(incidentMessage.data)
            }
            break

          case 'update':
            if (
              incidentMessage.data.id === currentIncident.value?.id &&
              hasParticipantName(incidentMessage.data)
            ) {
              deleteIncident()
            }
            break

          case 'delete':
            if (incidentMessage.data.id === currentIncident.value?.id) {
              deleteIncident()
            }
            break
        }
      }
    })
  }

  return {
    init,
    currentIncident,
  }
}
